import { GoogleTagManager } from '@next/third-parties/google';

import { currentLocaleConfig } from '@/lib/i18n/localeSpecific';
import { getTermlyScriptUrl } from '@/lib/termly/constants';
import { TermlyGate } from '@/lib/termly/TermlyGate';
import { getTermlyWebsiteUuid } from '@/lib/termly/TermlyScript';

import { isProd } from '@/constant/env';

import GTMOptimizer from './GTMOptimizer';

type DataLayerType = Record<string, unknown>;

type WindowWithDataLayer = Window & {
  dataLayer: DataLayerType[];
};

declare const window: WindowWithDataLayer;

export const sendPageViewEvent = (
  pathname: string,
  previousPathname: string | null
) => {
  sendGAEvent({
    event: 'page_view',
    page_title: document.title,
    page_path: pathname,
    previous_page_path: previousPathname,
  });
};

export const sendThirdPartySignUpEvent = (
  pathname: string,
  previewPathname: string | null,
  signup: 'github' | 'google'
) => {
  sendGAEvent({
    event: 'started_sign_up_flow',
    page_title: document.title,
    page_path: pathname,
    previous_page_path: previewPathname,
    sign_up_option: signup,
  });
};

export const sendVirtualPageViewEvent = (
  pathname: string,
  previousPathname: string | null
) => {
  sendGAEvent({
    event: 'virtual_page_view',
    page_title: document.title,
    page_path: pathname,
    previous_page_path: previousPathname,
  });
};

export function sendGAClickEvent(eventData: DataLayerType) {
  sendGAEvent({ event: 'element_click', eventData });
}

export function sendVideoEvent(eventName: string, videoData: DataLayerType) {
  sendGAEvent({
    event: eventName,
    ...videoData,
  });
}

export function sendGAEvent(eventData: DataLayerType) {
  if (!isProd) {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(eventData);
}

export const TrackingScripts = () => {
  const termlyWebsiteUuid = getTermlyWebsiteUuid();

  return (
    <TermlyGate
      scriptUrl={
        termlyWebsiteUuid ? getTermlyScriptUrl(termlyWebsiteUuid) : undefined
      }
    >
      <GoogleTagManager gtmId={currentLocaleConfig().gtmId} />
      <GTMOptimizer />
    </TermlyGate>
  );
};
